import {Dates, formatDateLocale} from '@hconnect/uikit'
import {OverlaySurfaceThemeProvider} from '@hconnect/uikit/src/lib2'
import {
  Calendar,
  DatePickerTextField,
  PickerForm,
  TabBar
} from '@hconnect/uikit/src/lib2/components/momentDatePicker'
import {Button, Checkbox, Popover, Box, FormControlLabel} from '@mui/material'
import {Moment} from 'moment-timezone'
import React, {useCallback, useMemo, useRef, useState} from 'react'

import {getResponsivePopoverStyle} from '../../styles'
import {ParticularShift, Shift, ShiftQuickSelectSlots} from '../../types'

import {QuickSelect} from './QuickSelect'
import {handleDateChange, ShiftDropdown, getShiftRange, momentShiftPickLabel} from './ShiftDropdown'

type DateRange = [Moment | null, Moment | null]

interface Props {
  className?: string
  date?: Moment | null
  dateRange?: DateRange
  quickSelectId?: string
  isOutsideRange?: (arg: Moment) => boolean
  label?: React.ReactNode
  onChange: (value) => void
  shifts: Shift[]
  timezone: string
  plantNow: Moment
  translate: (key: string) => string
  language: string
  quickSelectSlots?: ShiftQuickSelectSlots[]
  disableDateRangeSelect?: boolean
  disableTimeOnMultiDay?: boolean
}

enum TYPES {
  QUICK_SELECT = 'quickSelect',
  DATE = 'date',
  DATE_RANGE = 'dateRange'
}

const getSelectionType = (quickSelectId, date, dateRange) => {
  if (quickSelectId) return TYPES.QUICK_SELECT
  if (date) return TYPES.DATE
  if (dateRange) return TYPES.DATE_RANGE
  throw new Error('no value selected in date picker')
}

export const ShiftDatePicker: React.FC<Props> = (props) => {
  const {
    quickSelectId,
    date,
    dateRange = [],
    isOutsideRange,
    onChange,
    className,
    shifts,
    timezone,
    plantNow,
    translate,
    language,
    quickSelectSlots,
    disableDateRangeSelect = false,
    disableTimeOnMultiDay = false
  } = props

  const isSingleDate = date !== undefined
  const [startDate, endDate] = dateRange
  const [selectedStartDate, setSelectedStartDate] = useState<Moment | null>(
    date || startDate || null
  )
  const [selectedEndDate, setSelectedEndDate] = useState<Moment | null | undefined>(endDate)
  const [selectedTime, setSelectedTime] = useState<Dates>({
    startDate: date || startDate || null,
    endDate: endDate || null
  })
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [showCalendar, setShowCalendar] = useState(!quickSelectId)
  const [isRangeSelect, setShowRangeSelect] = useState(!isSingleDate && !disableDateRangeSelect)
  const [selectionType, setType] = useState(getSelectionType(quickSelectId, date, dateRange))
  const noDateText = translate('datePicker.chooseDate')

  const selectionText = useMemo(() => {
    if (selectionType === TYPES.QUICK_SELECT) {
      return translate(`shiftEvent.quickSelection.${quickSelectId}`)
    }
    if (selectionType === TYPES.DATE || !selectedEndDate) {
      return momentShiftPickLabel(selectedStartDate as Moment, {shifts})
    }
    if (selectionType === TYPES.DATE_RANGE) {
      return `${formatDateLocale(selectedStartDate as Moment, language)} - ${formatDateLocale(
        selectedEndDate,
        language
      )}`
    }
    return noDateText
  }, [
    selectionType,
    selectedEndDate,
    noDateText,
    translate,
    quickSelectId,
    selectedStartDate,
    shifts,
    language
  ])

  const close = useCallback(() => setOpen(false), [setOpen])

  const handleDateRangeChange = (dates: Dates, withTime?: boolean) => {
    const {startDate, endDate} = dates
    if (withTime) {
      setSelectedTime({
        startDate: startDate,
        endDate: endDate
      })
    }

    const newDates: Dates = {
      startDate:
        selectedTime.startDate && startDate && !withTime
          ? startDate
              .clone()
              .hours(selectedTime.startDate.hours())
              .minutes(selectedTime.startDate.minutes())
              .seconds(selectedTime.startDate.seconds())
              .milliseconds(selectedTime.startDate.milliseconds())
          : startDate,
      endDate:
        selectedTime.endDate && endDate && !withTime
          ? endDate
              .clone()
              .hours(selectedTime.endDate.hours())
              .minutes(selectedTime.endDate.minutes())
              .seconds(selectedTime.endDate.seconds())
              .milliseconds(selectedTime.endDate.milliseconds())
          : endDate
    }

    setSelectedStartDate(startDate)
    setSelectedEndDate(endDate)

    if (newDates.endDate) {
      onChange(newDates)
      setType(TYPES.DATE_RANGE)
    }
  }

  const handleSingleDateChange = (date: Moment | null) => {
    if (!date) return
    const updatedDate = getShiftRange(handleDateChange(date, {shifts}))
    setSelectedStartDate(updatedDate.startDate)
    setSelectedEndDate(updatedDate.startDate)
    onChange(updatedDate)
    setType(TYPES.DATE)
  }

  const calendarProps = {
    timezone,
    isOutsideRange,
    startDate: selectedStartDate,
    ...(isRangeSelect
      ? {endDate: selectedEndDate, onRangeChange: handleDateRangeChange}
      : {onDateChange: handleSingleDateChange})
  }

  return (
    <Box
      sx={{
        padding: 0,
        display: 'inline-block',
        position: 'relative',
        zIndex: 999,
        width: {
          xs: '100%',
          md: 'inherit'
        }
      }}
      className={className}
    >
      <DatePickerTextField
        buttonRef={anchorRef}
        focus={open}
        setFocus={setOpen}
        text={selectionText}
        label={translate('datePicker.label')}
      />

      <OverlaySurfaceThemeProvider>
        <Popover
          open={open}
          anchorEl={anchorRef.current}
          onClose={close}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          PaperProps={{
            sx: getResponsivePopoverStyle,
            id: 'simple-date-picker-popover-paper'
          }}
          data-test-id="simple-date-picker-popover"
        >
          <Box
            sx={{
              padding: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 356
            }}
          >
            <Box pb={1}>
              <TabBar setShowCalendar={setShowCalendar} showCalendar={showCalendar} />
            </Box>

            {showCalendar ? (
              <>
                <Calendar {...calendarProps} />
                <Box
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    width: '100%'
                  })}
                >
                  {!disableDateRangeSelect && (
                    <FormControlLabel
                      sx={{mb: 1}}
                      control={
                        <Checkbox
                          data-test-id="range-select"
                          checked={isRangeSelect}
                          onChange={() => setShowRangeSelect((prev) => !prev)}
                        />
                      }
                      label={translate('datePicker.selectRange')}
                    />
                  )}
                  {isRangeSelect ? (
                    <PickerForm
                      applyDateRange={(value: Dates) => {
                        handleDateRangeChange(value, true)
                        close()
                      }}
                      isOutsideRange={isOutsideRange}
                      isSingleDate={!isRangeSelect}
                      endDate={selectedEndDate}
                      startDate={selectedStartDate}
                      timeZone={timezone}
                      disableTimeOnMultiDay={disableTimeOnMultiDay}
                    />
                  ) : (
                    <>
                      {selectedStartDate && (
                        <ShiftDropdown
                          date={selectedStartDate}
                          shifts={shifts}
                          onChange={(particularShift: ParticularShift) => {
                            const range = getShiftRange(particularShift)
                            onChange(range)
                            setSelectedStartDate(range.startDate)
                            setType(TYPES.DATE)
                          }}
                          label={translate('datePicker.label')}
                          translate={translate}
                        />
                      )}
                      <Box display={'flex'} justifyContent={'center'} pt={2}>
                        <Button
                          variant={'contained'}
                          onClick={() => setOpen(false)}
                          data-test-id="single-day-apply"
                        >
                          {translate('datePicker.apply')}
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              </>
            ) : (
              <QuickSelect
                shifts={shifts}
                plantNow={plantNow}
                timezone={timezone}
                onChange={(val: ShiftQuickSelectSlots) => {
                  onChange(val)
                  setType(TYPES.QUICK_SELECT)
                  close()
                }}
                quickSelectId={quickSelectId}
                translate={translate}
                language={language}
                quickSelectSlots={quickSelectSlots}
              />
            )}
          </Box>
        </Popover>
      </OverlaySurfaceThemeProvider>
    </Box>
  )
}
